/** Cleaning for stored pretranslation values.
 *
 *  Lives in its own dependency-free module so it can be exercised directly
 *  by a test rather than through a bound map, a fetch and a Dexie read.
 *
 *  ── Why this exists ─────────────────────────────────────────────────────
 *  Thomas reported QualityLand 2.0 "still showing German alternating
 *  paragraphs". Two static analyses (a 99.4% paragraph match rate, a count
 *  of 1,574 short blocks) described real properties of the book that were
 *  not the bug. A screenshot settled it in seconds: every paragraph was on
 *  screen TWICE — German, then its English translation directly beneath.
 *  The translation was being added alongside the original, not substituted
 *  for it.
 *
 *  The reader was substituting correctly. The stored English values
 *  themselves contained the German. Found in the shipped artifact
 *  (4,626 pairs), both defects caused by storing raw model completions
 *  without parsing them:
 *
 *   1. RUNAWAY CONTINUATION (63 pairs). Given one short source block —
 *      usually a chapter heading — the generator translated it, then kept
 *      generating: echoing the German that followed and translating that
 *      too, until it hit a stop token. So the value for source "Der Alte"
 *      was stored as:
 *
 *        "The Old Man\n\n\n\nEr saß auf der Bank, die Sonne brannte…"
 *
 *      Headings open chapters, so he met one at the top of every chapter
 *      and each dumped a whole doubled section onto the page.
 *
 *   2. SENTINEL LEAK (13 pairs). A raw stop token glued to an otherwise
 *      correct translation, rendered as book text — he saw a literal
 *      `<|file_separator|>` at the end of a page.
 *
 *  2026-10-09.
 */

/** Raw model control tokens. Never book content.
 *
 *  WIDENED 2026-10-09: the first version of this pattern was
 *  /<\|[A-Za-z0-9_]+\|>/ and missed the malformed variants a truncated
 *  completion leaves behind — `<||im_start|>` (double pipe) and a bare
 *  `<|im` fragment. Three values survived the first repair pass because of
 *  it, so the earlier "218 -> 0" was true of the pattern tested rather than
 *  of stop tokens as such. */
const SENTINEL_RE = /<\|+[A-Za-z0-9_]*\|*>?/;

/** A leaked chat-template turn marker alone on a line. */
const ROLE_LINE_RE = /(?:^|\n)\s*(?:user|assistant|system)\s*(?:\n|$)/;

const MD_BOLD_RE = /\*\*([^*\n]+)\*\*/g;
const MD_ITAL_RE = /(?<!\*)\*([^*\n]+)\*(?!\*)/g;
const MD_UNDER_RE = /(?<![A-Za-z0-9_])_([^_\n]+)_(?![A-Za-z0-9_])/g;

/** A value this much longer than its source cannot be a translation of it.
 *  Derived from the measured 0.74–1.38x ratio of a genuine translation. */
function implausible(src: string, out: string): boolean {
  return out.length > 3 * src.length + 120;
}

/**
 * Clean one stored translation. Returns null if nothing usable is left.
 *
 * @param s the source paragraph this value is the translation of
 * @param e the stored translation, possibly contaminated
 */
export function sanitizeTranslation(s: string, e: string): string | null {
  let out = e || "";
  const src = s || "";

  // 1. Everything from the first leaked token onward is model scaffolding,
  //    not translation, so TRUNCATE rather than substitute. For a trailing
  //    token this is identical to stripping it; for one sitting mid-value it
  //    also removes the runaway that followed.
  {
    const cands: number[] = [];
    const ms = SENTINEL_RE.exec(out);
    if (ms) cands.push(ms.index);
    const mr = ROLE_LINE_RE.exec(out);
    if (mr) cands.push(mr.index);
    if (cands.length) out = out.slice(0, Math.min(...cands));
  }

  // 2. Runaway. Nothing legitimately renders a 20-character heading as 2,000
  //    characters of prose, so the length ratio alone establishes the
  //    overrun; requiring German in the discarded tail missed the runaways
  //    that ran away in English. Cut at the EARLIEST break — preferring the
  //    role marker put the cut *past* the German when a leaked 'assistant'
  //    sat deep inside the overrun.
  if (out.length > 300 && out.length >= 3 * Math.max(20, src.length)) {
    const cands: number[] = [];
    const m = ROLE_LINE_RE.exec(out);
    if (m) cands.push(m.index);
    const nl2 = out.indexOf("\n\n");
    if (nl2 > 0) cands.push(nl2);
    // A LONE newline is a valid seam only when the source has none — any
    // newline is then a model artifact. This salvages 17 heads that would
    // otherwise be discarded whole (Quixote idx 112 among them).
    if (!src.includes("\n")) {
      const nl1 = out.indexOf("\n");
      if (nl1 > 0) cands.push(nl1);
    }
    if (cands.length) {
      // Keep the head only if its length is plausible for a translation of
      // `s`. Measured over the affected pairs the kept head runs 0.74–1.38x
      // the source length (median 1.00), so this is not truncating real text.
      const head = out.slice(0, Math.min(...cands)).trim();
      if (head && !implausible(src, head)) out = head;
    }
  }

  // 3. Normalise SHAPE: same shape in, same shape out. A single-paragraph
  //    source must come back as a single paragraph — Thomas saw pages with
  //    one line on them and pages that scrolled, because a value carrying
  //    stray newlines becomes two or three rendered blocks and breaks the
  //    column. Markdown goes too: the panel paints plain text, so the
  //    model's `*emphasis*` rendered as literal asterisks in the book.
  if (!src.includes("*")) {
    out = out.replace(MD_BOLD_RE, "$1").replace(MD_ITAL_RE, "$1");
  }
  if (!src.includes("_")) out = out.replace(MD_UNDER_RE, "$1");
  if (!src.includes("\n")) {
    out = out.replace(/\s+/g, " ");
  } else {
    out = out.replace(/\n{3,}/g, "\n\n").replace(/[ \t]+/g, " ");
  }

  out = out.trim();
  if (!out) return null;

  // 4. Still grossly longer than its source? Then it is not a translation of
  //    it — it is the following passage, often still in the original
  //    language. Dropping lets the live translator cover the block, which is
  //    correct; serving it shows the wrong paragraph.
  if (implausible(src, out)) return null;

  // NOT IMPLEMENTED DELIBERATELY: dropping a pair whose value equals its
  // source. It reads like an untranslated echo, but every pair it actually
  // caught was a CORRECT identity translation — text already in English
  // inside the German original ("Straight outta Gotham City.", "What the
  // hell are you doing here?") and an onomatopoeia ("Schnick, Schnack,
  // Schnuck!"). 4 false positives, 0 true positives. The one genuinely
  // untranslated value in the book is a German paraphrase of its source
  // rather than a copy, so an equality test misses it regardless.
  return out;
}
